import CloseOutlined from "@mui/icons-material/CloseOutlined";
import Alert, { alertClasses } from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import SnackbarContent, {
	snackbarContentClasses,
} from "@mui/material/SnackbarContent";
import Stack from "@mui/material/Stack";
import type { SxProps, Theme } from "@mui/material/styles";
import {
	isValidElement,
	useState,
	type MouseEvent,
	type ReactNode,
	type SyntheticEvent,
} from "react";

/** Rendered inside every toast; `AppToaster` animates it for auto-closing toasts. */
export const TOAST_COUNTDOWN_CLASS = "served-toast-countdown";

export type ToastKind =
	| "default"
	| "message"
	| "loading"
	| "success"
	| "info"
	| "warning"
	| "error";

/** How long an action's `confirmation` label shows before the toast closes. */
export const TOAST_CONFIRMATION_MS = 1200;

/**
 * Mirrors sonner's `Action`, so call sites keep sonner's option shape. With a
 * `confirmation`, the button shows it once `onClick` succeeds and the toast closes
 * shortly after, so the user sees the action worked.
 */
export interface ToastActionButton {
	label: ReactNode;
	onClick: (event: MouseEvent<HTMLButtonElement>) => void | Promise<unknown>;
	confirmation?: ReactNode;
}

export interface ToastViewProps {
	kind: ToastKind;
	message: ReactNode;
	description?: ReactNode;
	icon?: ReactNode;
	action?: ReactNode | ToastActionButton;
	cancel?: ReactNode | ToastActionButton;
	closeButton?: boolean;
	onDismiss: () => void;
}

const isActionButton = (
	value: ReactNode | ToastActionButton,
): value is ToastActionButton =>
	typeof value === "object" &&
	value !== null &&
	!isValidElement(value) &&
	"onClick" in value;

const surfaceSx = {
	position: "relative",
	overflow: "hidden",
	width: "100%",
	boxShadow: 6,
} satisfies SxProps<Theme>;

/** As sonner: icon, text and actions share one vertical centre line. */
const alertSx = {
	...surfaceSx,
	alignItems: "center",
	[`& .${alertClasses.action}`]: { pt: 0 },
} satisfies SxProps<Theme>;

/**
 * As sonner, neutral toasts use the theme's own surface (dark in dark mode, light in
 * light mode) rather than Snackbar's inverted one. Long messages wrap beside the icon.
 */
const snackbarSx = {
	...surfaceSx,
	bgcolor: "background.paper",
	color: "text.primary",
	border: 1,
	borderColor: "divider",
	flexWrap: "nowrap",
	alignItems: "center",
	[`& .${snackbarContentClasses.message}`]: { flex: 1, minWidth: 0 },
} satisfies SxProps<Theme>;

const actionButtonSx = {
	whiteSpace: "nowrap",
	flexShrink: 0,
} satisfies SxProps<Theme>;
const snackbarActionSx: SxProps<Theme> = {
	...actionButtonSx,
	color: "primary.main",
};

const ToastActions = ({
	action,
	cancel,
	closeButton,
	isSnackbar,
	onDismiss,
}: Pick<ToastViewProps, "action" | "cancel" | "closeButton" | "onDismiss"> & {
	isSnackbar: boolean;
}) => {
	const [confirmed, setConfirmed] = useState(false);

	if (!action && !cancel && !closeButton) return null;

	// Same semantics as sonner: cancel always dismisses, action dismisses unless prevented.
	const renderButton = (
		button: ReactNode | ToastActionButton,
		onClicked: (event: SyntheticEvent) => void,
		sx: SxProps<Theme> = actionButtonSx,
	) =>
		isActionButton(button) ? (
			<Button
				size="small"
				color="inherit"
				sx={sx}
				disabled={confirmed}
				onClick={async (event) => {
					if (!button.confirmation) {
						void button.onClick(event);
						onClicked(event);
						return;
					}
					try {
						await button.onClick(event);
					} catch {
						return;
					}
					setConfirmed(true);
					setTimeout(onDismiss, TOAST_CONFIRMATION_MS);
				}}>
				{confirmed && button.confirmation ? button.confirmation : button.label}
			</Button>
		) : (
			button
		);

	return (
		<Stack
			direction="row"
			alignItems="center"
			gap={0.5}>
			{!!cancel && renderButton(cancel, onDismiss)}
			{!!action &&
				renderButton(
					action,
					(event) => {
						if (!event.defaultPrevented) onDismiss();
					},
					isSnackbar ? snackbarActionSx : undefined,
				)}
			{closeButton && (
				<IconButton
					size="small"
					color="inherit"
					aria-label="Close"
					onClick={onDismiss}>
					<CloseOutlined fontSize="small" />
				</IconButton>
			)}
		</Stack>
	);
};

const Countdown = () => (
	<Box
		component="span"
		aria-hidden
		className={TOAST_COUNTDOWN_CLASS}
	/>
);

/**
 * MUI rendering of a sonner toast: neutral toasts (default, message, loading) look like
 * a Snackbar, severity toasts are an `Alert`. Sonner still owns stacking, timers and swipe.
 */
export const ToastView = ({
	kind,
	message,
	description,
	icon,
	...actionProps
}: ToastViewProps) => {
	const isSnackbar =
		kind === "default" || kind === "message" || kind === "loading";
	const actions = (
		<ToastActions
			{...actionProps}
			isSnackbar={isSnackbar}
		/>
	);

	if (!isSnackbar)
		return (
			<Alert
				severity={kind}
				icon={icon}
				role={kind === "error" ? "alert" : "status"}
				action={actions}
				sx={alertSx}>
				{description ? (
					<>
						<AlertTitle>{message}</AlertTitle>
						{description}
					</>
				) : (
					message
				)}
				<Countdown />
			</Alert>
		);

	const leadingIcon =
		icon ??
		(kind === "loading" && (
			<CircularProgress
				size={18}
				color="inherit"
			/>
		));

	return (
		<SnackbarContent
			role="status"
			sx={snackbarSx}
			action={actions}
			message={
				<Stack
					direction="row"
					alignItems="center"
					gap={1.5}>
					{!!leadingIcon && (
						// Never squeezed by long messages.
						<Box sx={{ display: "flex", flexShrink: 0 }}>{leadingIcon}</Box>
					)}
					<Box sx={{ minWidth: 0 }}>
						<Box
							sx={{ fontWeight: description ? "fontWeightMedium" : undefined }}>
							{message}
						</Box>
						{!!description && <Box sx={{ opacity: 0.8 }}>{description}</Box>}
					</Box>
					<Countdown />
				</Stack>
			}
		/>
	);
};
